/**
 * The API client.
 *
 * Types come from `src/api/schema.d.ts`, generated from the service's own OpenAPI schema
 * (`npm run api`). They are never hand-written: the two sides no longer share a language,
 * so a hand-maintained set of interfaces drifts within weeks and the drift surfaces as a
 * runtime error during a live audit session.
 */

import type { components } from "./schema";

const BASE = "/api/v1";

/** RFC 9457 problem detail — `components.schemas.Problem` in the contract. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
  }
}

let token: string | null = null;

/**
 * The signed-in auditor's bearer token, for the session stream — M14-2. See
 * {@link accessLinkToken}; same reason, other credential.
 */
export function bearerToken(): string | null {
  return token;
}

/**
 * The in-memory slot, and only that. Persisting is `rememberSession`'s job and is deliberately
 * not done here: three flows set a credential that must **not** outlive the page — a credential
 * link, a confirmation link and a report delivery token are each explicitly not a session.
 */
export function setToken(value: string | null): void {
  token = value;
}

/**
 * The signed-in caller, as the door handed them back.
 *
 * The tenant and user ids come from the token *response* rather than being parsed out of the
 * token: what a client may reach is the server's decision, and the browser's copy is only for
 * addressing its own tenant's resources. The email is the one that was typed — the door does
 * not hand it back, and it is what the shell shows.
 */
export interface SignedIn {
  role: Schemas["Role"];
  email: string;
  /** Null for a `lead_auditor` — an auditor may hold more than one certification-body
   * relationship, so the token names none (multiple-CBs-per-auditor). Never null for `cb`
   * or `admin`. */
  tenantId: string | null;
  userId: string;
}

/** What is written to storage: the caller, the credential, and the hour it lapses in. */
interface StoredSignIn extends SignedIn {
  token: string;
  /** Epoch milliseconds, derived from the response's `expires_in` the moment it arrived. */
  expiresAt: number;
}

/**
 * Where the session is kept between page loads.
 *
 * It is kept at all because the token used to live only in the module variable above: a page
 * refresh dropped it and returned the caller to the door mid-audit, holding a credential the
 * server would have honoured for the rest of its hour.
 *
 * `sessionStorage` rather than `localStorage` because it is scoped to the one tab and is gone
 * when the browser closes, which is what a shared machine in a certification body's office
 * needs. The cost is that a second tab is a second sign-in, and it is small here: the app has
 * no router, and the three capability-bearing links carry their own credentials and reach
 * their screens without a session at all.
 *
 * A stored bearer token is readable by script where the in-memory one was not. What that buys
 * an attacker is replay for no longer than the token's own hour, and the HttpOnly cookie that
 * closes it belongs with the refresh-and-lifetime work — a live session in M4 outruns 60
 * minutes, and doing the cookie before then means doing it twice.
 */
const SIGN_IN_KEY = "complao.sign-in";

/** Notified when the server stops honouring the stored sign-in, so the app can leave. */
let signedOutHandler: (() => void) | null = null;

export function onSignedOut(handler: (() => void) | null): void {
  signedOutHandler = handler;
}

/**
 * Storage can be absent — disabled by policy, or throwing on access in a private window. The
 * app has to work there, and it works the way it did before this file persisted anything.
 */
function signInStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readSignedIn(): StoredSignIn | null {
  let raw: string | null = null;
  try {
    raw = signInStorage()?.getItem(SIGN_IN_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;

  let stored: StoredSignIn;
  try {
    stored = JSON.parse(raw) as StoredSignIn;
  } catch {
    forgetSignedIn();
    return null;
  }

  // A lapsed hour is discarded here rather than found out by a 401 in the middle of a screen.
  // The caller lands on the door, which is what an expired token means, instead of on a shell
  // whose every call fails.
  if (typeof stored.token !== "string" || typeof stored.expiresAt !== "number") {
    forgetSignedIn();
    return null;
  }
  if (Date.now() >= stored.expiresAt) {
    forgetSignedIn();
    return null;
  }
  return stored;
}

function rememberSignedIn(stored: StoredSignIn): void {
  try {
    signInStorage()?.setItem(SIGN_IN_KEY, JSON.stringify(stored));
  } catch {
    // A session that cannot be written is a session that does not survive a refresh. That is
    // the behaviour this file had before, so there is nothing to report and nothing to fail.
  }
}

function forgetSignedIn(): void {
  try {
    signInStorage()?.removeItem(SIGN_IN_KEY);
  } catch {
    // As above.
  }
}

/**
 * The signed-in caller a previous page load left behind, or null.
 *
 * Read by the app as it mounts. Expiry is checked on every read, so a token whose hour ran out
 * while the tab sat open is never handed back.
 */
export function currentSignedIn(): SignedIn | null {
  const stored = readSignedIn();
  if (!stored) return null;
  return {
    role: stored.role,
    email: stored.email,
    tenantId: stored.tenantId,
    userId: stored.userId,
  };
}

// Restored at module load, before anything renders: a request fired by a component's first
// effect has to carry the credential the caller already holds.
token = readSignedIn()?.token ?? null;

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  // Never on a multipart body: the boundary is part of the content type and only the
  // browser knows it, so setting the header by hand makes the request unparseable.
  if (init.body && typeof init.body === "string") headers.set("Content-Type", "application/json");
  // The caller's own credential wins over the session's. A credential link, a confirmation
  // link and a report delivery token are each deliberately not a session, and now that a
  // session outlives a page load there is a signed-in caller to send one of those links to.
  const onSession = !headers.has("Authorization") && token !== null;
  if (onSession) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });

  if (!response.ok) {
    // A refused session is not a message for a screen but the end of the session. The server
    // has stopped honouring the token — the hour lapsed, the credential was withdrawn, the
    // certification body was suspended — and a stored copy outliving that decision would keep
    // a signed-in shell drawn around a caller who is no longer one.
    if (response.status === 401 && onSession) {
      signOut();
      signedOutHandler?.();
    }
    // Every failure arrives in the same shape, including the regulatory refusals —
    // those carry the clause and the reason in `detail`, which is what gets shown.
    const problem = (await response.json().catch(() => null)) as Problem | null;
    throw new ApiError(
      problem ?? { type: "about:blank", title: response.statusText, status: response.status },
    );
  }

  return response;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await (await send(path, init)).json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body), headers }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: (path: string) => send(path, { method: "DELETE" }),
  // A DELETE that answers with a body, for the one call that confirms itself with a typed
  // reference rather than the bare id in the path — `api.delete` above stays untouched for
  // its four existing callers, all of which discard the response.
  deleteWithBody: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "DELETE", body: JSON.stringify(body) }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>(path, { method: "POST", body: form });
  },
};

type Schemas = components["schemas"];

export type TokenResponse = Schemas["TokenResponse"];
export type Role = Schemas["Role"];
export type CredentialLink = Schemas["CredentialLink"];
export type ApplicationDraft = Schemas["ApplicationDraft"];
export type ApplicationValidation = Schemas["ApplicationValidation"];
export type ApplicationStep = Schemas["ApplicationStep"];
export type ValidationIssue = Schemas["ValidationIssue"];
export type StepResult = Schemas["StepResult"];
export type Sector = Schemas["Sector"];
export type RegistryEntry = Schemas["RegistryEntry"];
export type TenantSummary = Schemas["TenantSummary"];

export async function authenticate(email: string, password: string): Promise<TokenResponse> {
  const result = await api.post<TokenResponse>("/auth/token", { email, password });
  setToken(result.access_token);
  rememberSignedIn({
    role: result.role,
    email,
    tenantId: result.tenant_id,
    userId: result.user_id,
    token: result.access_token,
    expiresAt: Date.now() + result.expires_in * 1000,
  });
  return result;
}

export function signOut(): void {
  setToken(null);
  forgetSignedIn();
}

/**
 * Register a certification body. The only call on this client that needs no token.
 *
 * Answers the same whether or not the address was already registered — see the endpoint.
 * Nothing usable comes back, so there is nothing to sign the caller in with: the account
 * is inert until a Complao administrator activates it.
 */
export async function signUp(email: string): Promise<string> {
  return (await api.post<Schemas["SignupResponse"]>("/auth/signup", { email })).detail;
}

/**
 * Read a set-password link — M9-0.
 *
 * The token travels in the `Authorization` header rather than in the path, so it never
 * reaches an access log or a `Referer`. It lives in the URL fragment until the moment it is
 * sent, which is the one place a browser keeps something the server never sees.
 *
 * This deliberately does **not** use `setToken`: a credential link is not a session, and
 * leaving it in the module's token would attach it to every later call.
 */
export async function readCredentialLink(link: string): Promise<CredentialLink> {
  return request<CredentialLink>("/auth/credential", {
    headers: { Authorization: `Bearer ${link}` },
  });
}

/**
 * Confirm the address a certification body registered with — M9-0 phase 6b.
 *
 * The token travels in the `Authorization` header, never in the path, and it is deliberately
 * not passed to `setToken`: confirming an address is not a session and admits nobody.
 */
export async function confirmRegistration(link: string): Promise<Schemas["RegistrationConfirmed"]> {
  return request<Schemas["RegistrationConfirmed"]>("/auth/confirm-registration", {
    method: "POST",
    headers: { Authorization: `Bearer ${link}` },
  });
}

/**
 * Spend the link and set the password.
 *
 * Nothing usable comes back and nothing is meant to: every refusal about who may sign in
 * lives at the door, so the screen sends them there afterwards with the password they have
 * just chosen.
 */
export async function setPasswordFromLink(link: string, password: string): Promise<string> {
  const result = await request<Schemas["PasswordSet"]>("/auth/credential", {
    method: "POST",
    body: JSON.stringify({ password }),
    headers: { Authorization: `Bearer ${link}`, "Content-Type": "application/json" },
  });
  return result.detail;
}

/**
 * Replace the password Complao issued — M9-0 §5.
 *
 * The route every account that predates the credential link migrates through, authenticated
 * by the password it replaces. It hands back nothing usable, so the screen sends them to the
 * sign-in they were just refused at.
 */
export async function changePassword(
  email: string,
  currentPassword: string,
  password: string,
): Promise<string> {
  const result = await api.post<Schemas["PasswordSet"]>("/auth/password-change", {
    email,
    current_password: currentPassword,
    password,
  });
  return result.detail;
}

/**
 * Ask for a reset link.
 *
 * Answers the same sentence whether or not the address exists — the endpoint's posture, and
 * the browser must not improve on it by reporting anything else.
 */
export async function requestPasswordReset(email: string): Promise<string> {
  return (await api.post<Schemas["ResetResponse"]>("/auth/password-reset", { email })).detail;
}

/** The certification body register. Administrators only; the platform's own row is not in it. */
export async function listTenants(): Promise<TenantSummary[]> {
  return (await api.get<Schemas["TenantList"]>("/admin/tenants")).values;
}

/**
 * Open or close a certification body's sign-in.
 *
 * Not verification — that is the Cl. 5.2 assessment of the accreditation, below. The
 * updated row comes back so the list shows what was stored rather than what the browser
 * assumed was stored.
 */
export async function setTenantActive(
  tenantId: string,
  active: boolean,
): Promise<TenantSummary> {
  return api.post<TenantSummary>(`/admin/tenants/${tenantId}/activation`, { active });
}

export type CredentialLinkIssued = Schemas["CredentialLinkIssued"];

/**
 * Screen 71 — send a certification body its sign-in link again — M9-0.
 *
 * Only for a body whose door is open: a link to a shut door would set a password the
 * account cannot use. Which kind goes is not the administrator's choice — a body that has
 * never set a password gets a first-password link, one that has gets a reset — and `purpose`
 * on the response says which went.
 */
export async function resendCredentialLink(tenantId: string): Promise<CredentialLinkIssued> {
  return api.post<CredentialLinkIssued>(`/admin/tenants/${tenantId}/credential-link`, {});
}

export type VerificationDecision = Schemas["VerificationDecision"];

/**
 * Open one body's application for verification — screens 10 and 11.
 *
 * Every call writes an access event naming the administrator to that body's own chain
 * (data dictionary §7). It is not a page the browser should prefetch or poll: each opening
 * is a reading of a certification body's file and is recorded as one.
 */
export async function readTenantApplication(tenantId: string): Promise<CertificationBody> {
  return api.get<CertificationBody>(`/admin/tenants/${tenantId}/application`);
}

/**
 * Verify, or hand the application back.
 *
 * Verifying also opens the door, because screen 10 offers one affirmative action and it
 * reads "Verify and activate" — but they stay two facts on the record, two columns and two
 * events. Requesting more information clears the submission so the body can correct and
 * resubmit, which is the only route back from a submitted application.
 */
export async function decideVerification(
  tenantId: string,
  decision: VerificationDecision,
  idempotencyKey: string,
): Promise<TenantSummary> {
  return api.post<TenantSummary>(`/admin/tenants/${tenantId}/verification`, decision, {
    "Idempotency-Key": idempotencyKey,
  });
}

/**
 * Open one piece of evidence, as bytes.
 *
 * The certificate is the thing being checked against the accreditation body's public
 * register, so screen 10 has to be able to open it rather than list its name. Fetched
 * through the client because the route is authenticated — an `<a href>` carries no bearer
 * token, and a link that needs one would have to put it in the URL.
 */
export async function fetchEvidence(tenantId: string, evidenceId: string): Promise<string> {
  const response = await send(`/admin/tenants/${tenantId}/evidence/${evidenceId}`);
  return URL.createObjectURL(await response.blob());
}

/** Registry names, active entries only — the values an application may choose from. */
export async function listRegistry(
  kind: "countries" | "timezones" | "accreditation-bodies",
): Promise<string[]> {
  const result = await api.get<Schemas["RegistryList"]>(`/reference/registries/${kind}`);
  return result.values.map((entry) => entry.name);
}

/** IAF technical sectors. The index is the stable identifier — never renumbered. */
export async function listSectors(): Promise<Sector[]> {
  return (await api.get<Schemas["SectorList"]>("/reference/sectors")).values;
}

/**
 * The standards in scope, from the engine rather than a constant here. Which standards
 * Complao audits is regulatory content, and a copy in the browser is a copy that can
 * disagree with the base-table mapping.
 */
export async function listStandards(): Promise<string[]> {
  return (await api.get<Schemas["StandardList"]>("/reference/standards")).values;
}

/**
 * Validate a part-completed application. Writes nothing.
 *
 * The application is submitted as one payload at the end of six steps, so requiredness
 * has to be answerable before there is anything to submit. Gating "Continue" on the
 * browser alone would make requiredness something a client can skip.
 */
export async function validateApplication(
  draft: ApplicationDraft,
): Promise<ApplicationValidation> {
  return api.post<ApplicationValidation>(
    "/onboarding/certification-bodies/validation",
    draft,
  );
}

export type CertificationBody = Schemas["CertificationBody"];
export type EvidenceEntry = Schemas["EvidenceEntry"];
export type DurationGuardrails = Schemas["DurationGuardrailSummary"];

const ONBOARDING = "/onboarding/certification-bodies";

/** The three record-retention options screen 06 offers. */
export async function listRetentionPolicies(): Promise<string[]> {
  return (await api.get<Schemas["RetentionPolicyList"]>("/reference/retention-policies")).values;
}

/**
 * The MD 5 floor and the Stage 1 split bounds.
 *
 * Fetched rather than written into this file. The floor is Cl. 2.1.2 read out of
 * `reference-data/`, and a copy in the browser is a copy that can disagree with the value
 * the server refuses a looser threshold against. The prototype's 75 is exactly that defect.
 */
export async function readDurationGuardrails(): Promise<DurationGuardrails> {
  return api.get<DurationGuardrails>("/reference/duration-guardrails");
}

/**
 * Save the application as far as it has been typed.
 *
 * Not the same thing as submitting it, and deliberately not written into the tenant's own
 * columns: the administrator's register reads `legal_name`, and a half-typed one would show
 * there as a claim the certification body never made.
 */
export async function saveDraft(draft: ApplicationDraft): Promise<void> {
  await api.put<Schemas["SavedDraft"]>(`${ONBOARDING}/draft`, draft);
}

export async function readDraft(): Promise<ApplicationDraft | null> {
  return (await api.get<Schemas["SavedDraft"]>(`${ONBOARDING}/draft`)).draft ?? null;
}

/** Evidence already attached to this application. */
export async function listEvidence(): Promise<EvidenceEntry[]> {
  return (await api.get<Schemas["EvidenceList"]>(`${ONBOARDING}/evidence`)).values;
}

/** Attach an accreditation certificate or insurance schedule. Content-addressed. */
export async function uploadEvidence(file: File): Promise<EvidenceEntry> {
  return api.upload<EvidenceEntry>(`${ONBOARDING}/evidence`, file);
}

export async function deleteEvidence(id: string): Promise<void> {
  await api.delete(`${ONBOARDING}/evidence/${id}`);
}

/** Upload a certification body mark and get back its content-addressed key. */
export async function uploadMark(tenantId: string, file: File): Promise<string> {
  const stored = await api.upload<Schemas["MarkUpload"]>(
    `/tenants/${tenantId}/branding/logo`,
    file,
  );
  return stored.logo_object_key;
}

/**
 * The stored mark, as a URL the browser can draw.
 *
 * Fetched rather than pointed at. An `<img src>` is a plain browser request and carries no
 * `Authorization` header, so the endpoint — which is authenticated, because a certification
 * body's mark is its own — would answer 401 every time. The bytes come back through the
 * same client as everything else and become a blob URL, which is revoked by the caller.
 */
export async function fetchMark(tenantId: string): Promise<string> {
  const response = await send(`/tenants/${tenantId}/branding/logo`);
  return URL.createObjectURL(await response.blob());
}

/**
 * Submit the application. One payload, at the end of six steps.
 *
 * The `Idempotency-Key` is the contract's, and it is what makes a retry safe: a repeated
 * key replays the answer and writes no second entry to the certification body's chain.
 */
export async function submitApplication(
  draft: ApplicationDraft,
  idempotencyKey: string,
): Promise<CertificationBody> {
  return api.post<CertificationBody>(ONBOARDING, draft, {
    "Idempotency-Key": idempotencyKey,
  });
}

/** The application as submitted — screen 08, and what it shows after a reload. */
export async function readApplication(): Promise<CertificationBody> {
  return api.get<CertificationBody>(ONBOARDING);
}

// --------------------------------------------------------------------------------------
// The auditor's own competence record — M1-5, screens 12 to 18
// --------------------------------------------------------------------------------------

export type CompetenceRecord = Schemas["CompetenceRecord"];
export type CompetenceValidation = Schemas["CompetenceValidation"];
export type CompetenceStep = Schemas["CompetenceStep"];
export type CompetenceStepResult = Schemas["CompetenceStepResult"];
export type CompetenceIssue = Schemas["CompetenceIssue"];
export type AuditorRecord = Schemas["AuditorRecord"];
export type InvitationResent = Schemas["InvitationResent"];

const AUDITORS = "/onboarding/auditors";

/**
 * Who the signed-in auditor is, and which certification body(ies) invited them.
 *
 * One record per certification-body membership — an auditor may hold more than one
 * (multiple-CBs-per-auditor). A newly invited auditor with a single, not-yet-completed
 * membership still gets an array of one; the caller decides what that means for the shell.
 */
export async function readMyAuditorRecord(): Promise<AuditorRecord[]> {
  return api.get<AuditorRecord[]>(`${AUDITORS}/me`);
}

/** One competence record, as the certification body verifying it reads it. CB-only. */
export async function readAuditorRecord(auditorId: string): Promise<AuditorRecord> {
  return api.get<AuditorRecord>(`${AUDITORS}/${auditorId}`);
}

/**
 * Validate a part-completed competence record. Writes nothing.
 *
 * The same reason the certification body's twin exists: the record is submitted as one
 * payload at the end of six steps, so requiredness has to be answerable before there is
 * anything to submit — and gating "Continue" in the browser alone would make it something
 * a client can skip.
 *
 * Addressed by `membershipId`, not the auditor's own id — an auditor may have more than one
 * certification-body relationship open at once, and this is which one is being completed.
 */
export async function validateCompetenceRecord(
  membershipId: string,
  record: CompetenceRecord,
): Promise<CompetenceValidation> {
  return api.post<CompetenceValidation>(`${AUDITORS}/${membershipId}/validation`, record);
}

export async function readCompetenceDraft(
  membershipId: string,
): Promise<CompetenceRecord | null> {
  const saved = await api.get<Schemas["SavedRecordDraft"]>(`${AUDITORS}/${membershipId}/draft`);
  return saved.draft ?? null;
}

export async function saveCompetenceDraft(
  membershipId: string,
  record: CompetenceRecord,
): Promise<void> {
  await api.put<Schemas["SavedRecordDraft"]>(`${AUDITORS}/${membershipId}/draft`, record);
}

/**
 * Sign and submit. Moves the auditor to `awaiting_verification` and no further —
 * self-assertion never activates an auditor, which is Cl. 7 and the contract's own words.
 */
export async function submitCompetenceRecord(
  membershipId: string,
  record: CompetenceRecord,
  idempotencyKey: string,
): Promise<AuditorRecord> {
  return request<AuditorRecord>(`${AUDITORS}/${membershipId}/submission`, {
    method: "PUT",
    body: JSON.stringify(record),
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

// --------------------------------------------------------------------------------------
// The auditor register — M1-6, screens 19, 20, 21
// --------------------------------------------------------------------------------------

export type AuditorSummary = Schemas["AuditorSummary"];
export type AuditorInvitation = Schemas["AuditorInvitation"];
export type CompetenceDecision = Schemas["CompetenceDecision"];
export type CompetenceVerification = Schemas["CompetenceVerification"];

/** Screen 19. Tenant-scoped by the database, not by the query. */
export async function listAuditors(): Promise<AuditorSummary[]> {
  return (await api.get<Schemas["AuditorList"]>("/auditors")).values;
}

/**
 * Screen 21 — create the account and propose standards.
 *
 * The invitation carries no competence. The auditor asserts that themselves and the
 * certification body verifies it afterwards, which is the whole shape of Cl. 7.
 */
export async function inviteAuditor(
  invitation: AuditorInvitation,
  idempotencyKey: string,
): Promise<AuditorRecord> {
  return api.post<AuditorRecord>(`${AUDITORS}/invitations`, invitation, {
    "Idempotency-Key": idempotencyKey,
  });
}

/**
 * Screen 21 — send an auditor their invitation again — M9-0.
 *
 * It re-issues rather than re-sends: only the digest of the first token was stored, so the
 * previous link stops working the moment this returns. Refused once the auditor has set a
 * password of their own — at that point *Forgot your password?* is theirs to use.
 */
export async function resendInvitation(auditorId: string): Promise<InvitationResent> {
  return api.post<InvitationResent>(`/auditors/${auditorId}/invitation`, {});
}

/**
 * Screen 20 — the Cl. 7 determination.
 *
 * Returns what was **dropped** as outside the certification body's accredited scope
 * alongside the record, because a determination that narrowed a claim has to be able to
 * say so.
 */
export async function verifyAuditor(
  auditorId: string,
  decision: CompetenceDecision,
  idempotencyKey: string,
): Promise<CompetenceVerification> {
  return api.post<CompetenceVerification>(`/auditors/${auditorId}/verification`, decision, {
    "Idempotency-Key": idempotencyKey,
  });
}

export type AccentMeasurement = Schemas["AccentMeasurement"];

/**
 * Measure an accent without storing it — what the brand step calls while onboarding.
 *
 * The ratio is computed by the same function that will later be written to the tenant, so
 * the number shown beside the colour picker is the number that gets stored. Measuring in
 * the browser would give a second implementation of a value the fallback decision turns
 * on, and two implementations of one number is one too many.
 */
export async function measureAccent(accent: string): Promise<AccentMeasurement> {
  const query = accent ? `?accent=${encodeURIComponent(accent)}` : "";
  return api.get<AccentMeasurement>(`/reference/accent-contrast${query}`);
}

// --------------------------------------------------------------------------------------
// M2-1 · The audits list — screen 23
// --------------------------------------------------------------------------------------

export type AuditSummary = Schemas["AuditSummary"];

/**
 * Screen 23. Tenant-scoped by the database, not by the query.
 *
 * The optional state narrows the list server-side rather than in the browser, so a filtered
 * view and an unfiltered one are the same row set seen through the same predicate.
 */
export async function listAudits(state?: string): Promise<AuditSummary[]> {
  const query = state ? `?state=${encodeURIComponent(state)}` : "";
  return (await api.get<Schemas["AuditList"]>(`/audits${query}`)).values;
}

export type RestartPermissionState = Schemas["RestartPermissionState"];

/**
 * Screen 23's Restart column — nominate an audit as restartable, or withdraw it.
 *
 * A testing affordance. There is one session per audit and no reopening, so an audit day
 * cannot be rehearsed twice; ticking this lets the lead auditor put *that* audit back to the
 * state its plan was issued in and run the day again.
 *
 * ⚠ **It is the only control in front of the restart**, which has no ceiling: a nominated
 * audit can be purged at any state, a signed and issued report included, along with the
 * transcript, every finding and the audit's own trail. The certification body declined both
 * a stop at the first signature and an environment switch, so this checkbox is it.
 *
 * Answers with the flag and the audit id rather than the aggregate — ticking a box on a list
 * of thirty audits must not read thirty determinations to hand back a boolean.
 */
export async function setRestartAllowed(
  auditId: string,
  allowed: boolean,
): Promise<RestartPermissionState> {
  return api.patch<RestartPermissionState>(`/audits/${auditId}/restart-allowed`, {
    allowed,
  });
}

export type AuditDeletionSummary = Schemas["AuditDeletionSummary"];

/**
 * Screen 23's Delete control — removes an audit outright, in any state.
 *
 * ⚖ A real, irrecoverable removal: every row in Postgres, the audit's documents in S3 and
 * its Bedrock knowledge base. `confirmReference` is the whole of the control — the caller
 * types the audit's own reference back, and the server refuses a mismatch.
 */
export async function deleteAudit(
  auditId: string,
  confirmReference: string,
): Promise<AuditDeletionSummary> {
  return api.deleteWithBody<AuditDeletionSummary>(`/audits/${auditId}`, {
    confirm_reference: confirmReference,
  });
}

// --------------------------------------------------------------------------------------
// M2-2 · Intake — screens 24, 25, 49
// --------------------------------------------------------------------------------------

export type IntakeDraft = Schemas["IntakeDraft"];
export type IntakeValidation = Schemas["IntakeValidation"];
export type IntakeIssue = Schemas["IntakeIssue"];
export type IntakeCard = Schemas["IntakeCard"];
export type AllocationAssessment = Schemas["AllocationAssessment"];
export type AllocationGate = Schemas["AllocationGate"];
export type OshmsSector = Schemas["OshmsSector"];
export type SavedIntakeDraft = Schemas["SavedIntakeDraft"];

/**
 * The Table OH&SMS 2 sectors, with the complexity each defaults to.
 *
 * A *second* sector list, and not a duplicate of `/reference/sectors`. Those are IAF
 * technical sectors — the accreditation taxonomy. These are the MD 5 complexity taxonomy,
 * and no published mapping joins them, so intake collects both (migration 0009).
 */
export async function listOshmsSectors(): Promise<OshmsSector[]> {
  return (await api.get<Schemas["OshmsSectorList"]>("/reference/oshms-sectors")).values;
}

export type FactorCatalogue = Schemas["FactorCatalogueSummary"];

/**
 * The MD 5 Cl. 8 catalogue, the Cl. 3.9 ceiling and the four adjustment types.
 *
 * The catalogue is served and the *magnitudes* are not, because MD 5 states none: the
 * weight of every factor is a certification body input carrying a mandatory justification.
 */
export async function readFactorCatalogue(): Promise<FactorCatalogue> {
  return api.get<FactorCatalogue>("/reference/duration-factors");
}

/**
 * Validate a part-completed intake. Writes nothing.
 *
 * Called on every change, because three of screen 24's answers are the server's to give:
 * whether the contractor field applies (Cl. 1.9), the live effective-personnel figure, and
 * the allocation gates. Each is regulatory content, and a browser computing any of them
 * would be holding a second copy of a clause.
 */
export async function validateIntake(draft: IntakeDraft): Promise<IntakeValidation> {
  return api.post<IntakeValidation>("/audits/validation", draft);
}

export async function createIntakeDraft(draft: IntakeDraft): Promise<SavedIntakeDraft> {
  return api.post<SavedIntakeDraft>("/audits/drafts", draft);
}

export async function readIntakeDraft(draftId: string): Promise<SavedIntakeDraft> {
  return api.get<SavedIntakeDraft>(`/audits/drafts/${draftId}`);
}

export async function saveIntakeDraft(
  draftId: string,
  draft: IntakeDraft,
): Promise<SavedIntakeDraft> {
  return api.put<SavedIntakeDraft>(`/audits/drafts/${draftId}`, draft);
}

export async function discardIntakeDraft(draftId: string): Promise<void> {
  await api.delete(`/audits/drafts/${draftId}`);
}

// --------------------------------------------------------------------------------------
// M2-4 and M2-5 · The determination and the two dialogs — screens 30 to 33
// --------------------------------------------------------------------------------------

export type AuditDetail = Schemas["AuditDetail"];
export type Determination = Schemas["Determination"];
export type ScopeSection = Schemas["ScopeSection"];
export type FactorLine = Schemas["FactorLine"];
export type AppliedAdjustment = Schemas["AppliedAdjustment"];
export type TrailEntry = Schemas["TrailEntry"];

/**
 * Submit the intake. The determination is made here, not on first read.
 *
 * Screen 30 is reached with the ledger already on it: every stage of the MD 5 pipeline is
 * in `duration_calculations` before this call returns. The `Idempotency-Key` is the
 * contract's — a repeated key writes no second entry to the audit's chain.
 */
export async function submitIntake(
  draft: IntakeDraft & { draft_id?: string },
  idempotencyKey: string,
): Promise<AuditDetail> {
  return api.post<AuditDetail>("/audits", draft, { "Idempotency-Key": idempotencyKey });
}

/** Screen 30 — the audit and its determination, read from where it was written. */
export async function readAudit(auditId: string): Promise<AuditDetail> {
  return api.get<AuditDetail>(`/audits/${auditId}`);
}

/**
 * Screen 31 — a departure from the determined scope.
 *
 * The whole audit comes back, not the override: an override changes the scope split and
 * the trail at once, and a client recomposing that from the row it posted would be
 * deriving what the server has just derived.
 */
export async function applyScopeOverride(
  auditId: string,
  clauseGroup: string,
): Promise<AuditDetail> {
  return api.post<AuditDetail>(`/audits/${auditId}/scope-overrides`, {
    clause_group: clauseGroup,
  });
}

export async function revertScopeOverride(
  auditId: string,
  clauseGroup: string,
): Promise<AuditDetail> {
  return (await send(`/audits/${auditId}/scope-overrides/${clauseGroup}`, {
    method: "DELETE",
  })).json() as Promise<AuditDetail>;
}

/**
 * Screen 32 — a certification body's departure from the calculated duration.
 *
 * Not a Cl. 8 factor. A factor is an MD 5 catalogue entry bounded by the Cl. 3.9 ceiling,
 * which is a `shall`; an adjustment sits outside it and is caught by the Cl. 2.1.2 floor,
 * which is a `should`. Below the floor the call still succeeds — screen 33 shows the
 * warning, and the departure is on the trail.
 */
/**
 * Screen 32's dialog, which no screen renders any more — M29 replaced it with the duration
 * field above. Kept because the route is: `POST /adjustments` is the only way to record a
 * departure *with* a type and a justification, `DurationAdjustment.reason`'s `minLength: 10`
 * is the contract's, and deleting the caller would leave the platform promising a surface
 * nothing here can reach.
 */
export async function applyAdjustment(
  auditId: string,
  adjustment: Schemas["AdjustmentRequest"],
): Promise<AuditDetail> {
  return api.post<AuditDetail>(`/audits/${auditId}/adjustments`, adjustment);
}

/**
 * Screen 30's duration field — set the agreed **Stage 1** duration, in hours.
 *
 * Stage 1 hours because that is the audit this platform runs: it is the figure the agenda
 * is built to sum to, the figure the approval names, and the figure the certification body
 * agrees with the client. The calculated audit time above it — its Cl. 3.9 ceiling and its
 * half-day rounding — is untouched, and the total is derived back from this at the split
 * ratio for the plan and the report.
 *
 * The server turns the target into one adjustment row and answers with the whole audit,
 * because a departure moves the ledger, the Cl. 2.1.2 comparison, the trail and both
 * approvals at once.
 */
export async function setAgreedDuration(
  auditId: string,
  hours: string,
): Promise<AuditDetail> {
  return api.put<AuditDetail>(`/audits/${auditId}/agreed-duration`, { hours });
}

/**
 * Screen 30's compensation field — the certification body's own time compensation.
 *
 * **How much to take off**, as a percentage of the *calculated* Stage 1 allocation, and a
 * magnitude rather than a signed figure: the field deducts and nothing else, so `50` means
 * fifty per cent off. The server refuses a negative and writes the row signed, at
 * `-deduction`, because the ledger sums signed values — the name of this argument is what
 * keeps the control's unit and the ledger's apart.
 *
 * It **replaces** rather than appends — the field holds one value — so the server withdraws
 * the compensation row this audit already has, on the trail, and writes one in its place.
 * `0` withdraws and writes nothing.
 */
export async function setCompensation(
  auditId: string,
  deduction: string,
): Promise<AuditDetail> {
  return api.put<AuditDetail>(`/audits/${auditId}/compensation`, { deduction });
}

export async function withdrawAdjustment(
  auditId: string,
  adjustmentId: string,
): Promise<AuditDetail> {
  return (await send(`/audits/${auditId}/adjustments/${adjustmentId}`, {
    method: "DELETE",
  })).json() as Promise<AuditDetail>;
}

/**
 * Apply the whole Cl. 8 factor set and re-run the determination.
 *
 * The set, not one factor: Cl. 3.9 caps the *total* reduction, so the ceiling is a
 * property of the set rather than of any member of it.
 */
export async function applyFactors(
  auditId: string,
  factors: Schemas["FactorRequest"][],
): Promise<AuditDetail> {
  return api.post<AuditDetail>(`/audits/${auditId}/factors`, { factors });
}

// --------------------------------------------------------------------------------------
// M3 · Approval, plan, readiness and the auditee portal — screens 34 to 51
// --------------------------------------------------------------------------------------

export type ApprovalState = Schemas["ApprovalState"];
export type ApprovalOutcome = Schemas["ApprovalOutcome"];
export type PlanState = Schemas["PlanState"];
export type AuditPlan = Schemas["AuditPlan"];
export type PlanDay = Schemas["PlanDay"];
export type PlanRow = Schemas["PlanRow"];
export type PlanDocument = Schemas["PlanDocument"];
export type PlanEdit = Schemas["PlanEdit"];
export type IctStanding = Schemas["IctStanding"];
export type GateState = Schemas["GateState"];
export type Readiness = Schemas["Readiness"];
/**
 * What an audit of these standards would normally expect the client to have.
 *
 * ⚠ **Advisory since ADR 0025.** These used to be *slots*: an upload named the one it
 * answered and the register read "7 of 11 received". Evidence is not bound to a clause any
 * more, so nothing arrives against one of these and nothing counts against it. It survives
 * because a client who does not know what to gather is otherwise left guessing.
 */
export type ExpectedDocumentRow = Schemas["ExpectedDocumentRow"];
export type DocumentRow = Schemas["DocumentRow"];
/**
 * One point of a preliminary review.
 *
 * ⚠ **Nothing writes these any more.** ADR 0025 removed the pre-audit evaluation; the table
 * and its restrictive policy are retained because rows exist for audits that already ran and
 * a report drafted against one of those still reads them.
 */
export type ReviewRow = Schemas["ReviewRow"];
export type SiteRow = Schemas["SiteRow"];
export type IssuedAccessLink = Schemas["IssuedAccessLink"];
export type ReissuedAccessLink = Schemas["ReissuedAccessLink"];
export type PortalView = Schemas["PortalView"];

/** Both Step 2 decisions. An absent one comes back as *pending*, never omitted. */
export async function listApprovals(auditId: string): Promise<ApprovalState[]> {
  return api.get<ApprovalState[]>(`/audits/${auditId}/approvals`);
}

/**
 * Approve the confirmed scope and the agreed duration — screen 37.
 *
 * No `party`: which approval this is comes from the caller's role, on the server. A party
 * in the body would let a certification body user record the lead auditor's approval,
 * which is the one thing dual approval exists to prevent.
 *
 * `agreedStage1Hours` is what the dialog was showing. The server refuses if the figure has
 * moved since — a dialog left open while an adjustment was applied cannot approve the old
 * one.
 */
export async function approveDetermination(
  auditId: string,
  idempotencyKey: string,
  agreedStage1Hours?: string,
): Promise<ApprovalOutcome> {
  return api.post<ApprovalOutcome>(
    `/audits/${auditId}/approvals`,
    { agreed_stage1_hours: agreedStage1Hours ?? null },
    { "Idempotency-Key": idempotencyKey },
  );
}

/**
 * The plan, or the reason there is none — screens 36 and 39.
 *
 * With one approval recorded this answers `blocked` with the approval outstanding, not a
 * 404 and not an error: the audit exists and the request is correct (assertion 1).
 */
export async function readPlan(auditId: string): Promise<PlanState> {
  return api.get<PlanState>(`/audits/${auditId}/plan`);
}

/** Screens 40 and 41 — one narrative field, flagged, with the generated wording retained. */
export async function editPlan(
  auditId: string,
  field: string,
  value: string,
): Promise<PlanState> {
  return api.patch<PlanState>(`/audits/${auditId}/plan`, { field, value });
}

/**
 * Screen 42 — approve the plan and issue the auditee's link.
 *
 * The token comes back here, and `readReadiness` hands back the same one for as long as the
 * link works — so a caller that drops it reads it again on Steps 4 and 5 rather than having
 * to re-issue, which would revoke the link the auditee is holding.
 */
export async function issuePlan(
  auditId: string,
  idempotencyKey: string,
  issuedTo?: string,
): Promise<IssuedAccessLink> {
  return api.post<IssuedAccessLink>(
    `/audits/${auditId}/plan/issue`,
    { issued_to: issuedTo ?? null },
    { "Idempotency-Key": idempotencyKey },
  );
}

/**
 * Screen 44 — the auditee never received their link — M9-0.
 *
 * **Destructive, and the button has to say so.** This mints a replacement, which revokes the
 * first — so an auditee who *is* holding the original loses it here.
 *
 * It is no longer the way to *read* the link: `readReadiness` carries the live token, so
 * this answers the narrower question it was always for — the link went to the wrong address,
 * or it has to be invalidated.
 *
 * `issuedTo` corrects the address. Omitted, the previous one is used again, which is the
 * ordinary case where the mail simply did not arrive.
 */
export async function resendAccessLink(
  auditId: string,
  idempotencyKey: string,
  issuedTo?: string,
): Promise<ReissuedAccessLink> {
  return api.post<ReissuedAccessLink>(
    `/audits/${auditId}/access-link/resend`,
    { issued_to: issuedTo ?? null },
    { "Idempotency-Key": idempotencyKey },
  );
}

/** Every IAF MD 4 gate on this audit. They attach to all of them, because ICT includes AI. */
export async function readIct(auditId: string): Promise<IctStanding> {
  return api.get<IctStanding>(`/audits/${auditId}/ict`);
}

export async function recordIctGate(
  auditId: string,
  gate: string,
  satisfied: boolean,
  payload: Record<string, unknown> = {},
): Promise<IctStanding> {
  return api.put<IctStanding>(`/audits/${auditId}/ict/${gate}`, { satisfied, payload });
}

export async function listSites(auditId: string): Promise<SiteRow[]> {
  return api.get<SiteRow[]>(`/audits/${auditId}/sites`);
}

/**
 * Declare a site — Cl. 4.2.7, and Cl. 3.1 Note 1 on the way in.
 *
 * A warehouse or a manufacturing location classified virtual is **refused**, not warned
 * about: a virtual site counts as a single site for MD 5 time calculation, so the
 * classification changes the determined duration.
 */
export async function declareSite(
  auditId: string,
  site: Schemas["SiteRequest"],
): Promise<SiteRow[]> {
  return api.post<SiteRow[]>(`/audits/${auditId}/sites`, site);
}

/**
 * The issued report, for the audit client it was issued to — M9-0, open item 71.
 *
 * The delivery token travels in the `Authorization` header, never in the path, and it is
 * deliberately **not** passed to `setToken`: a capability that reaches one report is not a
 * session, and leaving it in the module's token would attach it to every later call.
 *
 * The bytes are the copy that was stored when the report was issued, not a fresh render —
 * *the copy the client was sent* is the fact this route serves.
 */
export async function downloadIssuedReport(link: string): Promise<Blob> {
  const response = await send("/portal/report", {
    headers: { Authorization: `Bearer ${link}` },
  });
  return response.blob();
}

/** Screens 43 and 44 — one screen and two document counts. */
export async function readReadiness(auditId: string): Promise<Readiness> {
  return api.get<Readiness>(`/audits/${auditId}/readiness`);
}

export type IntakeRevision = Schemas["IntakeRevision"];

/**
 * Step 1, read back — the intake behind an audit that already exists.
 *
 * `AuditDetail` carries most of the intake and not the part that matters most here: the
 * four staff counts, the acknowledgements and the complexity justification. A revision
 * replaces the whole payload, so the form has to load the whole payload — prefilling from
 * the audit record alone would blank the fields feeding effective personnel.
 *
 * Reached by both parties. The lead auditor cannot revise it and can read it, because they
 * are asked to approve a band derived from counts that live nowhere else they can see.
 */
export async function readIntake(auditId: string): Promise<IntakeRevision> {
  return api.get<IntakeRevision>(`/audits/${auditId}/intake`);
}

/**
 * Revise the intake — screen 48, assertion 8.
 *
 * The determination is re-run, both approvals are withdrawn and the plan is superseded.
 * Steps 2 to 5 repeat.
 */
export async function reviseIntake(
  auditId: string,
  draft: IntakeDraft,
  idempotencyKey: string,
): Promise<AuditDetail> {
  return request<AuditDetail>(`/audits/${auditId}/intake`, {
    method: "PUT",
    body: JSON.stringify(draft),
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

// --------------------------------------------------------------------------------------
// The auditee's portal — screens 45 to 47, 50 and 51
// --------------------------------------------------------------------------------------

/**
 * The auditee holds a link, not an account, so the portal carries its own credential.
 *
 * Kept apart from `setToken` deliberately: the two are different kinds of thing and a
 * single slot would let a signed-in lead auditor's token be sent to a portal route, or an
 * access link be sent to a staff one.
 */
let accessLink: string | null = null;

export function setAccessLink(value: string | null): void {
  accessLink = value;
}

/**
 * The auditee's access token, for the session stream — M14-2.
 *
 * A browser cannot set headers on a WebSocket, so the credential has to be handed to the
 * socket explicitly rather than attached by this module the way `portal()` attaches it. A
 * getter rather than a second copy: one variable, set in one place, so a re-issued link
 * cannot leave a stale token behind on a screen.
 */
export function accessLinkToken(): string | null {
  return accessLink;
}

async function sendPortal(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && typeof init.body === "string") {
    headers.set("Content-Type", "application/json");
  }
  if (accessLink) headers.set("Authorization", `Bearer ${accessLink}`);

  const response = await fetch(`${BASE}/portal${path}`, { ...init, headers });
  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as Problem | null;
    throw new ApiError(
      problem ?? { type: "about:blank", title: response.statusText, status: response.status },
    );
  }
  return response;
}

async function portal<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await sendPortal(path, init)).json() as Promise<T>;
}

export async function readPortal(): Promise<PortalView> {
  return portal<PortalView>("");
}

/** Screen 45 — the Cl. 4.1.2 consent, given in the client's own name. */
export async function identifyAuditee(
  name: string,
  role: string,
  ictConsent: boolean,
): Promise<PortalView> {
  return portal<PortalView>("/identity", {
    method: "POST",
    body: JSON.stringify({ name, role, ict_consent: ictConsent }),
  });
}

/**
 * An upload, bound to whatever it answers.
 *
 * Three shelves and three meanings: `checklistItem` for a slot the plan requested before the
 * audit (screens 46 and 47), `clauseRef` for a request Complao raised *during* the session
 * (M14a-1), and neither for something the client sent unprompted. The server refuses both at
 * once — a file filed under two clauses is a file an assessor finds twice.
 */
export type UploadResult = Schemas["UploadResult"];
export type UploadOutcome = Schemas["UploadOutcome"];

/**
 * Everything the client has, in one request — M15-1, ADR 0025.
 *
 * One call rather than one per file, and the result is per file: a client who drags in twenty
 * documents with one bad file among them keeps the other nineteen, and each refusal names the
 * file it is about.
 *
 * `clauseRef` answers a request Complao raised during the session and closes it. It is not a
 * statement about what the documents are evidence of — nothing is filed by clause.
 */
export async function uploadPortalDocuments(
  files: File[],
  clauseRef?: string,
): Promise<UploadResult> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  if (clauseRef) form.append("clause_ref", clauseRef);
  return portal<UploadResult>("/documents", { method: "POST", body: form });
}

/** Screen 51 — Cl. 4.1.3. Stops the session *and* routes the audit to another method. */
export async function declineRemoteConduct(reason: string): Promise<PortalView> {
  return portal<PortalView>("/decline", {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// --------------------------------------------------------------------------------------
// M4 · The live session — screens 52 to 69
// --------------------------------------------------------------------------------------

export type SessionState = Schemas["SessionState"];
export type TranscriptMessage = Schemas["TranscriptMessage"];
export type RailRow = Schemas["RailRow"];
export type FindingRow = Schemas["FindingRow"];
export type RequestRow = Schemas["RequestRow"];
export type ParticipantRow = Schemas["ParticipantRow"];
export type CurrentCheckpoint = Schemas["CurrentCheckpoint"];
/* `Judgement` went with `POST /session/assessment` in M14b. The three values it carried —
 * adequate, insufficient, "I don't know" — are still the engine's, decided by the model and
 * by policy; nothing in the interface names one any more. */
export type FindingOutcome = "conforming" | "observation" | "minor_nc" | "major_nc";

/**
 * What a lead auditor did with a proposal — M14-4, dictionary §6.
 *
 * The three are not interchangeable and the report reads which one it was: `amended` says a
 * human changed a machine's grade, `rejected` says they overruled it and the row is kept as
 * the evidence of that. Together with `origin` this is the record of human oversight IAF
 * MD 4 Cl. 4.2.6 makes the report describe.
 */
export type FindingDisposition = "accepted_unchanged" | "amended" | "rejected";

/** Every grade a checkpoint can carry. There is no *not applicable* — dictionary §1. */
export const FINDING_OUTCOMES: { key: FindingOutcome; label: string; short: string }[] = [
  { key: "conforming", label: "Conforming", short: "C" },
  { key: "observation", label: "Observation", short: "OBS" },
  { key: "minor_nc", label: "Minor NC", short: "MNC" },
  { key: "major_nc", label: "Major NC", short: "MjNC" },
];

/**
 * The session console — screens 58, 68 and 69.
 *
 * `tenant` is an administrator's only. Reaching into a certification body's file is an
 * explicit act rather than a consequence of holding an administrator token, and the read
 * writes an access event to that body's own trail.
 */
export async function readSession(auditId: string, tenant?: string): Promise<SessionState> {
  const query = tenant ? `?tenant=${encodeURIComponent(tenant)}` : "";
  return api.get<SessionState>(`/audits/${auditId}/session${query}`);
}

/* ⚠ `assessAnswer` and `POST /session/assessment` were removed in M14b.
 *
 * The route took the lead auditor's sufficiency judgement, and the console offered it
 * whenever the last message was the client's — which since ADR 0022 is the whole twenty to
 * thirty seconds Complao spends judging. Two graders acted on one answer: the model closed a
 * clause and moved on while the auditor's press generated a narrowed question for the clause
 * it had left, which landed out of order and bound the client's next reply to the wrong
 * checkpoint.
 *
 * Sufficiency is the model's (ADR 0021), the grade waits in the tray, and an auditor who
 * wants to conduct a clause personally takes the floor (M14a-2). There is no third path.
 */


/** M4-6 — carry a Cl. 4.5 checkpoint out of the remote session, ungraded. */
export async function deferCheckpoint(
  auditId: string,
  reason: string,
): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/defer`, { reason });
}

/** Screens 59 and 60 — a lead auditor note. The reason is not optional. */
export async function addAuditorNote(
  auditId: string,
  body: string,
  reason: string,
): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/messages`, { body, reason });
}

/** Screens 63 to 65 — redirect, flag, add a finding, or pause. Reason mandatory for each. */
export async function intervene(
  auditId: string,
  intervention: {
    type: "note" | "redirect" | "flag" | "finding" | "pause";
    message?: string;
    reason: string;
    outcome?: FindingOutcome;
  },
): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/interventions`, {
    type: intervention.type,
    message: intervention.message ?? null,
    reason: intervention.reason,
    outcome: intervention.outcome ?? null,
  });
}

/** Screen 61 — the manual finding composer. Clause, grade, evidence and basis, all required. */
export async function recordFinding(
  auditId: string,
  finding: {
    clauseRef: string;
    outcome: FindingOutcome;
    evidence: string;
    basis: string;
  },
): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/findings`, {
    clause_ref: finding.clauseRef,
    outcome: finding.outcome,
    evidence: finding.evidence,
    basis: finding.basis,
  });
}

/** Screen 62's *Edit*. One card amended in place — a second row would double the tally. */
export async function amendFinding(
  auditId: string,
  amendment: {
    findingId: string;
    outcome: FindingOutcome;
    evidence: string;
    basis: string;
  },
): Promise<SessionState> {
  return api.patch<SessionState>(`/audits/${auditId}/session/findings`, {
    finding_id: amendment.findingId,
    outcome: amendment.outcome,
    evidence: amendment.evidence,
    basis: amendment.basis,
  });
}

/**
 * The lead auditor decides what Complao proposed — M14-4.
 *
 * A proposal is held back from the client until this runs. Accepting needs no reason;
 * amending and rejecting do, because changing or overruling what a machine proposed is the
 * oversight an accreditation assessor reads. ISO/IEC 17021-1 Cl. 9.4.5 — audit findings are
 * determined by the audit team.
 */
export async function disposeOfProposal(
  auditId: string,
  decision: {
    findingId: string;
    disposition: FindingDisposition;
    outcome?: FindingOutcome;
    statement?: string;
    reason?: string;
  },
): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/findings/disposition`, {
    finding_id: decision.findingId,
    disposition: decision.disposition,
    outcome: decision.outcome,
    statement: decision.statement,
    reason: decision.reason,
  });
}

/**
 * The lead auditor conducts this part of the audit personally — M14a-2.
 *
 * `held: true` and Complao stands down; the session stays live, the clock keeps running, the
 * client speaks freely and everything they say is read into the model's history. What stops
 * is Complao replying. Releasing does not answer the backlog — the next turn is driven by
 * the client's next reply.
 */
export async function holdTheFloor(
  auditId: string,
  held: boolean,
): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/floor`, { held });
}

/** Screen 66 — the clock restarts and Complao continues where it stopped. */
export async function resumeSession(auditId: string): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/resume`, {});
}

/**
 * Screen 58's Start Session control — opens the session ahead of the client.
 *
 * ⚖ Complao begins conducting immediately, on the certification body's own intake-time
 * ICT attestation rather than the auditee's own tick — a decision made against the
 * platform's original posture, confirmed after the tradeoff was named. The client's own
 * attendance and Cl. 4.1.2 confirmation are recorded separately when they join.
 */
export async function startSessionAsAuditor(auditId: string): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/start`, {});
}

/**
 * Restart the audit day — testing only, and the one call on this client that destroys.
 *
 * Returns the console re-read after the purge, which is why the screen needs no second
 * fetch: the session is gone and the state it answers with is the *awaiting the client*
 * one, so the console simply re-renders into it.
 *
 * ⚠ Removes the transcript, every finding with its origin and disposition, the report with
 * its signatures, the client's uploaded documents and the audit's trail, irrecoverably.
 * Available only on audits the certification body has ticked Restart against.
 */
export async function restartAuditDay(auditId: string): Promise<SessionState> {
  return api.post<SessionState>(`/audits/${auditId}/session/restart`, {});
}

/** The end of the session, and the record M5 consolidates from. */
export async function closeSession(
  auditId: string,
  idempotencyKey: string,
): Promise<SessionState> {
  return api.post<SessionState>(
    `/audits/${auditId}/session/close`,
    {},
    { "Idempotency-Key": idempotencyKey },
  );
}

// -- The auditee's half. Screens 52 to 54, 57 and 67 -------------------------------------

/** Screen 52 — the attendance record and the ICT confirmation, in the client's own name. */
export async function beginSession(
  participants: { name: string; role: string }[],
  ictConfirmed: boolean,
): Promise<SessionState> {
  return portal<SessionState>("/session", {
    method: "POST",
    body: JSON.stringify({ participants, ict_confirmed: ictConfirmed }),
  });
}

/** The console as the client sees it — screens 53, 54 and 67. */
export async function readAuditeeSession(): Promise<SessionState> {
  return portal<SessionState>("/session");
}

/** The client's reply. Refused while the audit is paused, and the refusal says why. */
export async function sendAuditeeMessage(body: string): Promise<SessionState> {
  return portal<SessionState>("/session/messages", {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

// -- M5 · Reporting and export. The report, its sign-off and its artefacts ---------------

export type ReportView = Schemas["ReportView"];
export type ReportDocument = Schemas["ReportDocument"];
export type ReportSectionRow = Schemas["ReportSectionRow"];
export type ReportTallyRow = Schemas["ReportTallyRow"];
export type ReportFinding = Schemas["ReportFinding"];
export type ReportNarrative = Schemas["ReportNarrative"];
export type ReportSignatureRow = Schemas["ReportSignatureRow"];
export type UnassessedRow = Schemas["UnassessedRow"];
export type IctDisclosure = Schemas["IctDisclosure"];
export type ExportResult = Schemas["ExportResult"];
export type IntegrityCheck = Schemas["IntegrityCheck"];

/**
 * The report, for whichever party is asking.
 *
 * Before consolidation this answers with `state: null` rather than failing: a closed
 * session with no report is an ordinary state and it is the one the lead auditor acts on.
 * Every `can_*` flag on the answer is the server's, so the screen renders no control the
 * routes would refuse — and the routes refuse anyway.
 */
export async function readReport(auditId: string): Promise<ReportView> {
  return api.get<ReportView>(`/audits/${auditId}/report`);
}

/** M5-1 — section-level roll-up, worst outcome wins, one finding set for every count. */
export async function draftReport(
  auditId: string,
  idempotencyKey: string,
): Promise<ReportView> {
  return api.post<ReportView>(
    `/audits/${auditId}/report/draft`,
    {},
    { "Idempotency-Key": idempotencyKey },
  );
}

/** The words are the auditor's. The tally and the clause list are not editable. */
export async function editReport(
  auditId: string,
  field: string,
  value: string,
): Promise<ReportView> {
  return api.patch<ReportView>(`/audits/${auditId}/report`, { field, value });
}

/** ADR 0008's *review and amend*, after the session has closed. In place, never a second row. */
export async function amendReportFinding(
  auditId: string,
  amendment: {
    findingId: string;
    outcome: FindingOutcome;
    evidence: string;
    basis: string;
  },
): Promise<ReportView> {
  return api.patch<ReportView>(`/audits/${auditId}/report/findings`, {
    finding_id: amendment.findingId,
    outcome: amendment.outcome,
    evidence: amendment.evidence,
    basis: amendment.basis,
  });
}

/**
 * M5-4 — the signature, taken over the exact wording that was presented.
 *
 * The attestation is sent back rather than a boolean, and the server compares it. A
 * checkbox would let a client sign wording it never rendered, which is the one thing a
 * sign-off has to rule out.
 */
export async function signReport(
  auditId: string,
  attestation: string,
  idempotencyKey: string,
): Promise<ReportView> {
  return api.post<ReportView>(
    `/audits/${auditId}/report/signoff`,
    { signature: attestation },
    { "Idempotency-Key": idempotencyKey },
  );
}

/** `cb_reviewed` — the certification body's technical review, which follows the signature. */
export async function reviewReport(
  auditId: string,
  idempotencyKey: string,
): Promise<ReportView> {
  return api.post<ReportView>(
    `/audits/${auditId}/report/review`,
    {},
    { "Idempotency-Key": idempotencyKey },
  );
}

/** Released to the client. **Not** a certification decision — that is the body's alone. */
export async function issueReport(
  auditId: string,
  idempotencyKey: string,
): Promise<ReportView> {
  return api.post<ReportView>(
    `/audits/${auditId}/report/issue`,
    {},
    { "Idempotency-Key": idempotencyKey },
  );
}

/** M5-8 — the one path back from a signature. The row is retained, never deleted. */
export async function withdrawSignature(
  auditId: string,
  reason: string,
): Promise<ReportView> {
  return api.post<ReportView>(`/audits/${auditId}/report/withdraw-signature`, { reason });
}

/**
 * M5-6 — the grant is resolved from role x report state, never held as a permission.
 *
 * `format` defaults to `"pdf"`. `"docx"` is refused for anything but `kind: "plan"` while
 * the plan is a draft (ADR 0028) — the report stays PDF in every state.
 */
export async function exportArtefact(
  auditId: string,
  kind: "plan" | "report",
  format?: "pdf" | "docx",
): Promise<ExportResult> {
  return api.post<ExportResult>(`/audits/${auditId}/exports`, { kind, format });
}

/**
 * The bytes an export produced.
 *
 * Fetched rather than linked, because the download carries the bearer token and an
 * `<a href>` would not. The object key is never in the URL: it is the SHA-256 of the bytes,
 * and a store addressable by digest is a store readable by guessing one.
 */
export async function downloadExport(auditId: string, exportId: string): Promise<Blob> {
  return (await send(`/audits/${auditId}/exports/${exportId}/content`)).blob();
}

/** M5-7 — Complao reaching into a certification body's file, and saying so on their trail. */
export async function breakGlassExport(
  auditId: string,
  tenantId: string,
  request: {
    kind: "plan" | "report";
    justification: string;
    suppressNotification?: boolean;
    suppressionApproverId?: string;
  },
  idempotencyKey: string,
): Promise<ExportResult> {
  return api.post<ExportResult>(
    `/admin/audits/${auditId}/exports?tenant=${tenantId}`,
    {
      kind: request.kind,
      justification: request.justification,
      suppress_notification: request.suppressNotification ?? false,
      suppression_approver_id: request.suppressionApproverId ?? null,
    },
    { "Idempotency-Key": idempotencyKey },
  );
}

/** The chain recomputed and compared, with no readable document produced. */
export async function verifyIntegrity(
  auditId: string,
  tenantId: string,
): Promise<IntegrityCheck> {
  return api.get<IntegrityCheck>(`/admin/audits/${auditId}/integrity?tenant=${tenantId}`);
}

// ------------------------------------------------------------------------------------
// M6 · Complao administration — screens 70, 72 and 73
// ------------------------------------------------------------------------------------

export type CrossTenantAudit = Schemas["CrossTenantAudit"];
export type CrossTenantAudits = Schemas["CrossTenantAudits"];
export type AdminAuditRecord = Schemas["AuditRecord"];
export type PlatformSettings = Schemas["PlatformSettings"];

/**
 * Screen 70 — every audit on the platform, across every certification body.
 *
 * **This read writes.** Each body whose audits appear gets `tenant.audits_listed` on its own
 * append-only chain, naming the references that were disclosed. There is no "quiet" form of
 * this call, and refreshing the screen records another read — which is why the screen says
 * so above the table rather than leaving it to be discovered.
 */
export async function listAllAudits(): Promise<CrossTenantAudits> {
  return api.get<CrossTenantAudits>("/admin/audits");
}

/**
 * Screen 72 — one audit record, reached inside one named certification body.
 *
 * Assertion 15: an access event naming the administrator is written to that audit's own
 * trail before anything is read, and it cannot be removed. The trail this returns already
 * carries it as its first entry.
 */
export async function readAuditRecord(
  auditId: string,
  tenantId: string,
): Promise<AdminAuditRecord> {
  return api.get<AdminAuditRecord>(`/admin/audits/${auditId}/record?tenant=${tenantId}`);
}

/** Screen 73 — the platform's two settings, with the bounds they are held to. */
export async function readPlatformConfig(): Promise<PlatformSettings> {
  return api.get<PlatformSettings>("/admin/config");
}

/**
 * Save screen 73.
 *
 * The floor may be raised above MD 5 Cl. 2.1.2's 80% and never lowered below it. The browser
 * does not restate that limit — `regulatory_floor` comes back with the values, because the
 * prototype restates it three times and gets it wrong in all three.
 */
export async function savePlatformConfig(settings: {
  followUpCap: number;
  durationThresholdFloor: number;
}): Promise<PlatformSettings> {
  return api.put<PlatformSettings>("/admin/config", {
    follow_up_cap: settings.followUpCap,
    duration_threshold_floor: settings.durationThresholdFloor,
  });
}

export type CostingReport = Schemas["CostingReport"];
export type AuditCost = Schemas["AuditCost"];
export type CostServiceTotal = Schemas["ServiceTotal"];

/**
 * M34 — what every audit on the platform cost to run, opened from screen 73.
 *
 * **This read writes**, exactly as `listAllAudits` does. Each certification body whose
 * audits are priced gets `tenant.costing_read` on its own append-only chain naming the
 * references that appeared. It is a *different* event type from `tenant.audits_listed`
 * because it discloses a different thing — what an audit consumed rather than who it is
 * for — and the screen says so above the table rather than leaving it to be found.
 *
 * **The period is always explicit and `end` is exclusive.** There is no default window on
 * the server, deliberately: a screen that quietly reported "the last 30 days" would make
 * two administrators comparing figures on different days disagree without either being
 * wrong.
 */
export async function readCosting(period: {
  start: string;
  end: string;
}): Promise<CostingReport> {
  return api.get<CostingReport>(
    `/admin/costing?start=${period.start}&end=${period.end}`,
  );
}

export type AuditPersona = Schemas["Persona"];
export type AuditPersonaSection = Schemas["PersonaSection"];
export type AuditPersonaInvariant = Schemas["PersonaInvariant"];

/**
 * Screen 73's persona card — the words Complao conducts an audit in.
 *
 * `invariants` and `composed` come back with the sections and the browser assembles neither.
 * The preamble and the two locked blocks are composed on the server, so the prompt shown to
 * an administrator is the prompt the model is sent rather than a reconstruction of it — the
 * same reason `regulatory_floor` travels with the threshold rather than being restated here.
 */
export async function readAuditPersona(): Promise<AuditPersona> {
  return api.get<AuditPersona>("/admin/persona");
}

/**
 * Publish a version. Append-only: nothing is amended and nothing is deleted.
 *
 * **Every section goes, including the unchanged ones.** A version is a whole snapshot rather
 * than a patch, because the row has to be readable on its own a year from now by somebody
 * asking what an audit was conducted under. The server refuses a partial set rather than
 * filling the gaps from the live version.
 */
export async function saveAuditPersona(update: {
  sections: Record<string, string>;
  note: string;
}): Promise<AuditPersona> {
  return api.put<AuditPersona>("/admin/persona", {
    sections: update.sections,
    note: update.note,
  });
}

export type StandardDocument = Schemas["StandardDocumentSummary"];
export type StandardDocumentList = Schemas["StandardDocumentList"];
export type DocumentBundle = Schemas["DocumentBundleSummary"];
export type DocumentBundleList = Schemas["DocumentBundleList"];

/**
 * Screen 73's third card — every reference document held, flat (M12 redesign).
 *
 * No more per-standard grouping: a document is no longer categorised at upload, and which
 * standard, or combination of standards, it answers for is a bundle's business
 * (`listDocumentBundles`/`saveDocumentBundle`), decided separately.
 */
export async function listStandardDocuments(): Promise<StandardDocumentList> {
  return api.get<StandardDocumentList>("/admin/standards/documents");
}

/**
 * Upload one or more reference documents into the flat pool, staged and edited in the
 * browser first — each entry carries its own optional Edition / Published on / Licence
 * reference / Licence held by, edited per file before "Upload" is ever clicked.
 *
 * No `standard` and no `title` — the filename identifies each file. One bad file never
 * fails the batch: check `result.results` for which of them were accepted.
 */
export type BulkUploadOutcome = Schemas["BulkUploadOutcome"];
export type BulkUploadResult = Schemas["BulkUploadResult"];

export async function uploadStandardDocuments(
  entries: Array<{
    file: File;
    edition?: string;
    publishedOn?: string;
    licenceReference?: string;
    licenceHolder?: string;
  }>,
): Promise<BulkUploadResult> {
  const form = new FormData();
  for (const entry of entries) form.append("files", entry.file);
  form.append(
    "metadata",
    JSON.stringify(
      entries.map((entry) => ({
        edition: entry.edition || null,
        published_on: entry.publishedOn || null,
        licence_reference: entry.licenceReference || null,
        licence_holder: entry.licenceHolder || null,
      })),
    ),
  );

  return request<BulkUploadResult>("/admin/standards/documents", {
    method: "POST",
    body: form,
  });
}

/**
 * Delete a reference document — the row, the S3 object and its own knowledge base, all
 * removed. Reverses ADR 0017 decision seven, by product decision. Confirmed by the caller
 * (a plain `window.confirm` on screen 73) before this is ever called — there is no reason
 * body, unlike every other destructive action on the platform.
 */
export async function deleteStandardDocument(documentId: string): Promise<void> {
  await api.delete(`/admin/standards/documents/${documentId}`);
}

/**
 * Every standards-combination and the documents mapped to it — the what-and-how mapping
 * (M12 redesign). A document is retrieved for a live session only where it is a named
 * member of the bundle matching that audit's exact standard combination.
 */
export async function listDocumentBundles(): Promise<DocumentBundleList> {
  return api.get<DocumentBundleList>("/admin/standards/document-bundles");
}

/**
 * Create or replace a standards-combination's documents — one call replaces the whole set.
 * `standards` order does not matter; the platform normalizes before matching.
 *
 * `bundleId` carries the caller's own intent, not something the server infers from
 * `standards`: omitted, this must be a brand new combination and the server refuses (409)
 * if one for `standards` already exists; passed, it names exactly which combination to
 * replace, and the server refuses (409) if a different one already claims `standards`.
 */
export async function saveDocumentBundle(
  standards: string[],
  documentIds: string[],
  bundleId?: string,
): Promise<DocumentBundle> {
  return api.put<DocumentBundle>("/admin/standards/document-bundles", {
    standards,
    document_ids: documentIds,
    bundle_id: bundleId ?? null,
  });
}

/**
 * Delete a standards-combination outright. Its documents are untouched — only the grouping
 * goes. A plain confirm, no reason recorded, the same posture `deleteStandardDocument` takes.
 */
export async function deleteDocumentBundle(bundleId: string): Promise<void> {
  await api.delete(`/admin/standards/document-bundles/${bundleId}`);
}

/**
 * The document itself, as a blob URL the caller revokes.
 *
 * The lead auditor reads the evidence the audit is about. Fetched rather than linked,
 * because the route is authenticated — an `<a href>` carries no bearer token, and a link
 * that needed one would have to put it in the URL.
 */
export async function fetchAuditDocument(
  auditId: string,
  documentId: string,
): Promise<string> {
  const response = await send(
    `/audits/${auditId}/readiness/documents/${documentId}/content`,
  );
  return URL.createObjectURL(await response.blob());
}

/**
 * Every document the client sent, as one zip — a blob URL the caller revokes.
 *
 * One request rather than one per file: a lead auditor working through eighteen uploads
 * should not press eighteen times, and eighteen authenticated fetches would each carry the
 * whole file into memory anyway. The server names the archive after the client, so the
 * filename comes back on the response rather than being composed here.
 */
export async function fetchAuditDocumentArchive(
  auditId: string,
): Promise<{ url: string; filename: string }> {
  const response = await send(`/audits/${auditId}/readiness/documents/archive`);
  return {
    url: URL.createObjectURL(await response.blob()),
    // A blob URL carries no headers, so the name has to be read off the response and put
    // on the anchor by hand — otherwise the browser saves it under a random UUID.
    filename: archiveName(response.headers.get("Content-Disposition")),
  };
}

/** The `filename="…"` of a Content-Disposition, or a name that is at least honest. */
function archiveName(disposition: string | null): string {
  const match = /filename="([^"]+)"/.exec(disposition ?? "");
  return match?.[1] ?? "documents.zip";
}

/**
 * The auditee's own copy of their own upload, as a blob URL the caller revokes.
 *
 * `fetchAuditDocument` carries the lead auditor's bearer token and nothing else — an auditee
 * holds a portal access link instead, so this goes through `sendPortal`, the same credential
 * `uploadPortalDocuments` sends the file with in the first place.
 */
export async function fetchPortalDocument(documentId: string): Promise<string> {
  const response = await sendPortal(`/documents/${documentId}/content`);
  return URL.createObjectURL(await response.blob());
}

/**
 * Mark one document reviewed, or put the mark back — M15-6.
 *
 * The lead auditor's own worklist marker. **Nothing runs behind it** — no evaluation, no
 * validation, no retrieval; anything more would be a pre-audit review by another name, which
 * ADR 0025 removed on purpose. Idempotent: setting the mark a document already carries writes
 * nothing and puts nothing on the trail.
 */
export async function markDocumentReviewed(
  auditId: string,
  documentId: string,
  reviewed: boolean,
): Promise<SessionState> {
  return api.patch<SessionState>(
    `/audits/${auditId}/session/documents/${documentId}`,
    { reviewed },
  );
}

/**
 * Mark every document in the register reviewed, or put every mark back.
 *
 * The same marker as `markDocumentReviewed` over the whole register. The trail still records
 * one entry per document — *which* evidence the auditor confirmed they read is the question
 * it answers, and "all of them" stops being true when the next upload arrives — so this is a
 * shorthand for the auditor, not a coarser fact. Documents already carrying the mark are
 * untouched.
 */
export async function markAllDocumentsReviewed(
  auditId: string,
  reviewed: boolean,
): Promise<SessionState> {
  return api.patch<SessionState>(`/audits/${auditId}/session/documents`, { reviewed });
}

/** A blob URL for the stored bytes. Admin-only; there is no certification body route. */
export async function fetchStandardDocument(documentId: string): Promise<string> {
  const response = await send(`/admin/standards/documents/${documentId}/content`);
  return URL.createObjectURL(await response.blob());
}
